// iter-289 - Payment methods picker page (/pay/:invoiceId).
// Fetches enabled+ready providers from /api/payment-methods, shows them
// as cards, and creates a payment link via /api/payments/create-link
// when the merchant picks one. Redirects to the checkout_url.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, AlertCircle, ShieldCheck, CreditCard, Wallet, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PayInvoice() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  const [invoice, setInvoice] = useState(null);
  const [providers, setProviders] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);  // iter-290
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingFor, setCreatingFor] = useState(null); // provider id being processed
  // iter-302: user must first SELECT a method, then press the "Pay" button.
  // This prevents accidental redirects/charges triggered by a single tap.
  const [selectedMethod, setSelectedMethod] = useState(null);

  const token = () => localStorage.getItem('business_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // iter-296 — robust parallel loading. Previously we used Promise.all
      // which failed the whole page if any one hop timed out (502/504
      // bubbled up as Cloudflare 522 in production). Now each fetch is
      // independent — the picker still renders when wallet/invoice are
      // temporarily unreachable, and we show a precise error only for
      // the piece that failed.
      const [methodsRes, invRes, earningsRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/payment-methods`, { headers: authHeader(), timeout: 15000 }),
        axios.get(`${BACKEND_URL}/api/agent-invoices/mine`, { headers: authHeader(), timeout: 15000 }),
        axios.get(`${BACKEND_URL}/api/agent/earnings-summary`, { headers: authHeader(), timeout: 15000 }),
      ]);

      // Providers — show them even if invoice lookup failed. The merchant
      // can at least see which gateways are live.
      if (methodsRes.status === 'fulfilled') {
        setProviders(methodsRes.value.data?.providers || []);
      }

      // Wallet balance — soft-fail: default to 0 so the wallet card still
      // renders (with a 0 balance) if earnings-summary is flaky.
      if (earningsRes.status === 'fulfilled') {
        const bal = Number(earningsRes.value.data?.available_balance ?? 0);
        setWalletBalance(bal < 0 ? 0 : bal);
      }

      // Invoice — the critical piece. Without it we can't create a payment.
      if (invRes.status === 'fulfilled') {
        const inv = (invRes.value.data?.invoices || []).find((i) => i.id === invoiceId);
        if (!inv) {
          setError(language === 'ar' ? 'الفاتورة غير موجودة أو لا تخصّك' : 'Invoice not found or not yours');
        } else {
          setInvoice(inv);
        }
      } else {
        const detail = invRes.reason?.response?.data?.detail || invRes.reason?.message || 'network';
        setError(
          (language === 'ar' ? 'تعذّر تحميل الفاتورة: ' : 'Could not load invoice: ') + String(detail).slice(0, 140)
        );
      }
    } catch (e) {
      // Shouldn't reach here with Promise.allSettled, but keep a safety net.
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, language]);

  useEffect(() => { load(); }, [load]);

  // iter-302: `pick` is now JUST a selection — it stores the picked method
  // in state so the UI can highlight it. Actual payment is triggered when
  // the user presses the "Pay" button at the bottom (see `confirmPay`).
  const pick = (provider) => {
    setError('');
    setSelectedMethod(provider);
  };

  // iter-302: actually trigger the payment for the previously selected method.
  const confirmPay = async () => {
    if (!invoice || !selectedMethod) return;
    const provider = selectedMethod;

    // Wallet is a special in-app path, not an ASB606 payment link.
    if (provider.provider === 'wallet') {
      return pickWallet(provider);
    }

    setCreatingFor(provider.provider);
    setError('');
    try {
      // iter-309 — Force the gateway to return to the CloudGM Business
      // frontend by passing our own origin. Prevents the "return to admin
      // dashboard" screen the hub shows otherwise on cancel/success.
      const origin = window.location.origin;
      const invRef = invoice.id;
      const r = await axios.post(
        `${BACKEND_URL}/api/payments/create-link`,
        {
          provider: provider.provider,
          amount: Number(invoice.remaining_amount ?? invoice.total_amount ?? invoice.total ?? 0),
          currency: invoice.currency || 'AED',
          description: (invoice.services || []).map((s) => s.service_type_ar || s.service_type_en || s.description).filter(Boolean).join(' - ') || `Invoice ${invoice.invoice_no_display || invoice.id}`,
          reference: invRef,
          success_url: `${origin}/payment-success?inv=${encodeURIComponent(invRef)}`,
          cancel_url: `${origin}/payment-cancelled?inv=${encodeURIComponent(invRef)}&reason=user_cancelled&provider=${encodeURIComponent(provider.provider)}`,
        },
        { headers: authHeader(), timeout: 20000 },
      );
      const url = r.data?.checkout_url;
      if (!url) throw new Error('Missing checkout_url in response');
      window.location.href = url;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to create payment link');
      setCreatingFor(null);
    }
  };

  // iter-290: wallet payment path. Deducts from the merchant's in-app
  // balance via /api/payments/pay-from-wallet, then redirects back to
  // /payments with a success toast. No ASB606 hop needed.
  const pickWallet = async (walletCard) => {
    if (!invoice) return;
    const amtDue = Number(invoice.remaining_amount ?? invoice.total_amount ?? invoice.total ?? 0);
    const confirmMsg = language === 'ar'
      ? `سيُخصم ${amtDue.toFixed(2)} درهم من محفظتك لتسديد الفاتورة ${invoice.invoice_no_display || ''}. هل توافق؟`
      : `${amtDue.toFixed(2)} AED will be deducted from your wallet to settle invoice ${invoice.invoice_no_display || ''}. Confirm?`;
    if (!window.confirm(confirmMsg)) return;

    setCreatingFor('wallet');
    setError('');
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/payments/pay-from-wallet`,
        { invoice_id: invoice.id },
        { headers: authHeader(), timeout: 20000 },
      );
      const newBal = Number(r.data?.new_balance ?? 0);
      toast.success(
        language === 'ar'
          ? `تم تسديد الفاتورة. الرصيد المتبقي: ${newBal.toFixed(2)} درهم`
          : `Invoice paid. New balance: AED ${newBal.toFixed(2)}`
      );
      // Return to the payments page so the merchant sees the invoice
      // moved out of "Due".
      navigate('/payments');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Insufficient-wallet details come as "INSUFFICIENT_WALLET|avail|need|msg"
      if (typeof detail === 'string' && detail.startsWith('INSUFFICIENT_WALLET|')) {
        const parts = detail.split('|');
        const avail = parts[1], need = parts[2];
        setError(language === 'ar'
          ? `الرصيد غير كافٍ. المتاح ${avail} درهم، المطلوب ${need} درهم`
          : `Insufficient balance. Available ${avail} AED, need ${need} AED`);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(e.message || 'Failed to pay from wallet');
      }
      setCreatingFor(null);
    }
  };

  const amount = invoice
    ? Number(invoice.remaining_amount ?? invoice.total_amount ?? invoice.total ?? 0)
    : 0;
  const currency = invoice?.currency || 'AED';
  const fmt = (n) => new Intl.NumberFormat(language === 'ar' ? 'ar-AE' : 'en-AE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);

  return (
    <div
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      className={`min-h-screen cgm-page`}
      data-testid="pay-invoice-page"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/payments')}
          className={`inline-flex items-center gap-1 text-sm mb-4 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="pay-back-btn"
        >
          <ArrowLeft className={`h-4 w-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
          {language === 'ar' ? 'رجوع إلى المدفوعات' : 'Back to payments'}
        </button>

        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="pay-loading">
            <Loader2 className="h-6 w-6 animate-spin opacity-60" />
          </div>
        )}

        {!loading && error && !invoice && (
          <div
            className={`p-6 rounded-xl border ${isDarkMode ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}
            data-testid="pay-error"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">{language === 'ar' ? 'تعذّر التحميل' : 'Unable to load'}</span>
            </div>
            <p className="text-sm mb-3">{error}</p>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
              data-testid="pay-retry-btn"
            >
              {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}

        {!loading && invoice && (
          <>
            {/* Amount card */}
            <div
              className={`rounded-2xl p-6 mb-6 ${isDarkMode ? 'bg-gradient-to-br from-[#131B2C] to-[#0F1524] border border-white/10' : 'bg-white shadow-xl border border-black/5'}`}
              data-testid="pay-amount-card"
            >
              <div className={`text-xs uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {language === 'ar' ? 'إجمالي المبلغ' : 'Total due'}
              </div>
              <div
                className={`text-4xl sm:text-5xl font-extrabold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                data-testid="pay-amount-value"
              >
                {currency} {fmt(amount)}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-mono text-xs opacity-80">
                  {invoice.invoice_no_display || invoice.id?.slice(0, 12)}
                </span>
                {invoice.services?.[0] && (
                  <span className="ms-2">
                    · {invoice.services[0].service_type_ar || invoice.services[0].service_type_en || invoice.services[0].description}
                  </span>
                )}
              </div>
            </div>

            {/* Provider picker */}
            <h2 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {language === 'ar' ? 'اختر طريقة الدفع' : 'Choose payment method'}
            </h2>

            {/* iter-302 — Unified card grid (Wallet FIRST, then providers).
                All cards share identical dimensions. The user selects a
                method here, then presses the "Pay" button below. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pay-methods-grid">
              {/* Wallet card (always first) */}
              {(() => {
                const canWalletPay = walletBalance >= amount && amount > 0;
                const isSelected = selectedMethod?.provider === 'wallet';
                return (
                  <button
                    type="button"
                    onClick={() => canWalletPay && pick({ provider: 'wallet' })}
                    disabled={!canWalletPay || creatingFor !== null}
                    data-testid="provider-wallet"
                    aria-pressed={isSelected}
                    className={`group relative text-start p-4 rounded-xl border transition-all min-h-[108px] ${
                      !canWalletPay
                        ? (isDarkMode ? 'bg-white/5 border-white/10 opacity-60 cursor-not-allowed' : 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed')
                        : isSelected
                          ? (isDarkMode
                              ? 'bg-emerald-500/15 border-emerald-400 ring-2 ring-emerald-400/50 shadow-lg shadow-emerald-500/10'
                              : 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/40 shadow-lg')
                          : (isDarkMode
                              ? 'bg-[#131B2C] border-emerald-500/30 hover:border-emerald-400 hover:-translate-y-0.5'
                              : 'bg-white border-emerald-200 shadow-sm hover:border-emerald-400 hover:shadow-lg hover:-translate-y-0.5')
                    }`}
                  >
                    {isSelected && (
                      <span className="absolute top-2 end-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500 text-white">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <div
                        className="h-12 w-12 rounded-lg flex items-center justify-center text-white shrink-0"
                        style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                      >
                        <Wallet className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`flex items-center gap-2 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'ar' ? 'المحفظة' : 'Wallet'}
                          {canWalletPay && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              isDarkMode ? 'bg-emerald-500/30 text-emerald-200' : 'bg-emerald-200 text-emerald-800'
                            }`}>
                              {language === 'ar' ? 'فوري' : 'Instant'}
                            </span>
                          )}
                        </div>
                        <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'الرصيد المتاح' : 'Available balance'}: <span className={`font-semibold ${canWalletPay ? 'text-emerald-500' : (isDarkMode ? 'text-rose-400' : 'text-rose-600')}`}>AED {fmt(walletBalance)}</span>
                        </div>
                        {!canWalletPay && amount > 0 && (
                          <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>
                            {language === 'ar'
                              ? `الرصيد غير كافٍ (المطلوب AED ${fmt(amount)})`
                              : `Insufficient (need AED ${fmt(amount)})`}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })()}

              {/* ASB606 providers (Stripe, Tamara, Ziina, ...) */}
              {providers.map((p) => {
                const feeVisible = p.fee_enabled && Number(p.fee_percent) > 0;
                const belowMin = amount < (Number(p.min_amount) || 0);
                const aboveMax = p.max_amount ? amount > Number(p.max_amount) : false;
                const clickable = !belowMin && !aboveMax && creatingFor === null;
                const isSelected = selectedMethod?.provider === p.provider;
                return (
                  <button
                    key={p.provider}
                    type="button"
                    disabled={!clickable}
                    onClick={() => pick(p)}
                    data-testid={`provider-${p.provider}`}
                    aria-pressed={isSelected}
                    className={`group relative text-start p-4 rounded-xl border transition-all min-h-[108px] ${
                      !clickable
                        ? (isDarkMode ? 'bg-white/5 border-white/5 opacity-60 cursor-not-allowed' : 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed')
                        : isSelected
                          ? (isDarkMode
                              ? 'bg-sky-500/15 border-sky-400 ring-2 ring-sky-400/50 shadow-lg shadow-sky-500/10'
                              : 'bg-sky-50 border-sky-500 ring-2 ring-sky-500/40 shadow-lg')
                          : (isDarkMode
                              ? 'bg-[#131B2C] border-white/10 hover:border-white/30 hover:-translate-y-0.5'
                              : 'bg-white border-black/5 shadow-sm hover:shadow-lg hover:-translate-y-0.5')
                    }`}
                  >
                    {isSelected && (
                      <span className="absolute top-2 end-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-sky-500 text-white">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      {p.logo_url ? (
                        <img
                          src={p.logo_url}
                          alt={p.display_name_en || p.provider}
                          className="h-12 w-12 rounded-lg object-contain bg-white p-1 shrink-0"
                        />
                      ) : (
                        <div
                          className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold shrink-0"
                          style={{ background: p.color || '#38BDF8' }}
                        >
                          {(p.display_name_en || p.provider)[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'ar' ? (p.display_name_ar || p.display_name_en) : (p.display_name_en || p.display_name_ar)}
                        </div>
                        <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? (p.description_ar || '') : (p.description_en || '')}
                        </div>

                        {(feeVisible || belowMin || aboveMax) && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {feeVisible && (
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}
                                data-testid={`fee-badge-${p.provider}`}
                              >
                                {language === 'ar' ? `+ رسوم ${p.fee_percent}%` : `+${p.fee_percent}% fee`}
                              </span>
                            )}
                            {belowMin && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                {language === 'ar' ? `الحد الأدنى ${p.min_amount} ${currency}` : `Min ${p.min_amount} ${currency}`}
                              </span>
                            )}
                            {aboveMax && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                {language === 'ar' ? `الحد الأعلى ${p.max_amount} ${currency}` : `Max ${p.max_amount} ${currency}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {providers.length === 0 && walletBalance < amount && (
              <div
                className={`mt-3 p-6 rounded-xl border text-center ${isDarkMode ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                data-testid="pay-no-providers"
              >
                <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">
                  {language === 'ar'
                    ? 'لا توجد بوابات دفع خارجية مفعّلة حالياً'
                    : 'No external payment gateways enabled right now'}
                </p>
              </div>
            )}

            {/* iter-302 — Pay button. Disabled until a method is selected.
                 Shows the picked method so the user confirms before paying. */}
            <div className="mt-5">
              <button
                type="button"
                onClick={confirmPay}
                disabled={!selectedMethod || creatingFor !== null}
                data-testid="pay-confirm-btn"
                className={`w-full h-14 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                  !selectedMethod || creatingFor !== null
                    ? (isDarkMode ? 'bg-white/5 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : 'bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white hover:shadow-xl hover:shadow-[#38BDF8]/30 hover:-translate-y-0.5'
                }`}
              >
                {creatingFor !== null ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {language === 'ar' ? 'جارِ المعالجة…' : 'Processing…'}
                  </>
                ) : selectedMethod ? (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    {language === 'ar'
                      ? `ادفع ${currency} ${fmt(amount)}`
                      : `Pay ${currency} ${fmt(amount)}`}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    {language === 'ar' ? 'اختر طريقة الدفع أولاً' : 'Select a payment method first'}
                  </>
                )}
              </button>
              {selectedMethod && creatingFor === null && (
                <p
                  className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  data-testid="pay-selected-info"
                >
                  {language === 'ar' ? 'الطريقة المختارة:' : 'Selected method:'}{' '}
                  <span className="font-semibold">
                    {selectedMethod.provider === 'wallet'
                      ? (language === 'ar' ? 'المحفظة' : 'Wallet')
                      : (language === 'ar'
                          ? (selectedMethod.display_name_ar || selectedMethod.display_name_en || selectedMethod.provider)
                          : (selectedMethod.display_name_en || selectedMethod.display_name_ar || selectedMethod.provider))}
                  </span>
                </p>
              )}
            </div>

            {/* Inline error after picking */}
            {error && invoice && (
              <div
                className={`mt-4 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-500/10 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-700 border border-red-200'}`}
                data-testid="pay-inline-error"
              >
                <AlertCircle className="h-4 w-4 inline me-1" />
                {error}
              </div>
            )}

            {/* Trust footer */}
            <div className={`mt-6 flex items-center justify-center gap-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              <ShieldCheck className="h-3.5 w-3.5" />
              {language === 'ar'
                ? 'المدفوعات آمنة ومشفّرة عبر بوابات معتمدة'
                : 'Payments are secure and encrypted via certified gateways'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
